<template>
  <v-row>
    <v-col cols="12" v-if="visibility.budgetAllocation">
      <river-chart
        :data="data.invest.media_spend"
        :titleString="`${$t('components.PeriodicDetail.budgetAllocationAndMediaMix')} (${spendUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.budgetAllocationAndMediaMix')} (${spendUnitName})`"
        x-name=""
        :y-name="$t('components.PeriodicDetail.spend')"
        icon="chart-bell-curve"
        :export="exportData"
        :colors="colors"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :sum-one-hundred="false"
        :hide-legend="hideLegend"
        :period="period" />
    </v-col>
    <v-col cols="12" v-if="visibility.kpiByPeriod">
      <bar-chart
        :data="dataSalesTimeArrays"
        :title="`${customKpiName} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${customKpiName} (${currentOutputUnitName})`"
        icon="chart-bell-curve"
        :export="exportData"
        :category-names="[]"
        :x-axis-type-is-date="true"
        :x-axis-rotate="40"
        :show-labels="dataSalesTimeArrays.x.length < 13"
        color="#1d953f"
        :precision="data.precision.sales_time"
        :grid-bottom-margin="20"
        :period="period" />
    </v-col>
    <v-col cols="12" v-if="visibility.salesDecomposition">
      <stacked-bar-chart
        :data="sales.sales_decomposition_by_period"
        :title="`${$t('components.PeriodicDetail.salesDecomposition', { kpi: customKpiName })} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.salesDecomposition', { kpi: customKpiName })} (${currentOutputUnitName})`"
        :precision="data.precision.sales_decomposition_by_period"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :colors="currentClientObject.colors" />
    </v-col>
    <v-col cols="12" v-if="visibility.mediaEffect">
      <river-chart
        :data="sales.media_attributed_sales_decomposition_by_period"
        :titleString="`${$t('components.PeriodicDetail.mediaEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.mediaEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.media_attributed_sales_decomposition_by_period"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :colors="colors"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :hide-legend="hideLegend"
        :period="period" />
    </v-col>
    <v-col cols="12" v-if="visibility.competitionEffect">
      <stacked-bar-chart
        :data="sales.competition_effect"
        :title="`${$t('components.PeriodicDetail.competitionEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.competitionEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.competition_effect"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :pretty-names="client.pretty_names ? client.pretty_names.externalVariables : null"
        :colors="currentClientObject.colors" />
    </v-col>
    <v-col cols="12" v-if="visibility.economyAndOthersEffect">
      <stacked-bar-chart
        :data="sales.economy_and_others_effect"
        :title="`${$t('components.PeriodicDetail.economyAndOthersEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.economyAndOthersEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.economy_and_others_effect"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :pretty-names="client.pretty_names ? client.pretty_names.externalVariables : null"
        :colors="currentClientObject.colors" />
    </v-col>
    <v-col cols="12" v-if="visibility.pricingEffect">
      <stacked-bar-chart
        :data="sales.pricing_effect"
        :title="`${$t('components.PeriodicDetail.pricingEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.pricingEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.pricing_effect"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :pretty-names="client.pretty_names ? client.pretty_names.externalVariables : null"
        :colors="currentClientObject.colors" />
    </v-col>
    <v-col cols="12" v-if="visibility.distributionEffect">
      <stacked-bar-chart
        :data="sales.distribution_effect"
        :title="`${$t('components.PeriodicDetail.distributionEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.distributionEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.distribution_effect"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :pretty-names="client.pretty_names ? client.pretty_names.externalVariables : null"
        :colors="currentClientObject.colors" />
    </v-col>
    <v-col cols="12" v-if="visibility.seasonalityAndOutliersEffect">
      <stacked-bar-chart
        :data="sales.seasonality_and_outliers_effect"
        :title="`${$t('components.PeriodicDetail.seasonalityAndOutliersEffect')} (${currentOutputUnitName})`"
        :export-image-name="`${client.name} - ${simulation.name} - ${$t('components.PeriodicDetail.seasonalityAndOutliersEffect')} (${currentOutputUnitName})`"
        :precision="data.precision.seasonality_and_outliers_effect"
        x-name=""
        :y-name="customKpiName"
        icon="chart-bell-curve"
        :export="exportData"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="65"
        :period="period"
        :show-legend="!hideLegend"
        :colors="currentClientObject.colors" />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import RiverChart from '@/components/RiverChart.vue'
import StackedBarChart from '@/components/StackedBarChart.vue'
import BarChart from '@/components/BarChart.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { resolvePeriodicSales, periodicDetailVisibility } from '@/utils/periodicDetailSections.js'

export default {
  data () {
    return {

    }
  },
  mixins: [prettyNames],
  methods: {

  },
  computed: {
    ...mapGetters('clients', [
      'currentClientObject'
    ]),
    // Which charts render. Shared with the Admin tab's hidden-sections list, which evaluates the
    // same rules for the Monthly and Weekly views while neither is mounted.
    visibility () {
      return periodicDetailVisibility(this.data, this.currentOutput)
    },
    sales () {
      const basePathsComponents = [
        'sales_decomposition_by_period',
        'sales_decomposition_total.components'
      ]
      return this.prettyTranslateNames(
        resolvePeriodicSales(this.data, this.currentOutput),
        basePathsComponents,
        this.client.pretty_names ? this.client.pretty_names.components : {}
      )
    },
    dataSalesTimeArrays: function () {
      const sortedArray = Object.entries(this.sales.time).slice().sort((a, b) => new Date(a[0]) - new Date(b[0]))
      let x = []
      let y = []

      sortedArray.forEach(a => {
        x.push(a[0])
        y.push(a[1])
      })
      return {
        x: x,
        y: y
      }
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    simulation: {
      type: Object,
      required: true
    },
    period: {
      type: String,
      required: true
    },
    currentOutput: {
      type: String,
      required: false
    },
    currentOutputUnitName: {
      type: String,
      required: true
    },
    spendUnitName: {
      type: String,
      required: true
    },
    colors: {
      type: Object,
      required: true
    },
    exportData: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    hideLegend: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    customKpiName: {
      type: String,
      required: true
    }
  },
  components: {
    RiverChart,
    StackedBarChart,
    BarChart
  }
}
</script>
